import axios from 'axios'
import { DlInstance } from '@/lib/axios'

const PORTAL_TOKEN_KEY = 'exportPortalToken'
const PORTAL_TOKEN_EXPIRES_KEY = 'exportPortalTokenExpires'

export type ExportPortalBillSummary = {
  id: string
  bill_date: string
  bill_number: string
  customer_name: string
  customer_id?: string | null
  total_amount: number
  paid_amount: number
  remaining_amount: number
  payment_status: string
  sale_mode: string
}

export type ExportPortalCustomerOption = {
  id: string
  name: string
}

export type ExportPortalFilters = {
  year: number
  month: number
  bill_type: 'buy' | 'sell'
  location: 'chanthaburi' | 'chumphon'
  customer_id?: string
}

export type BillExportTemplate =
  | 'buy_monthly'
  | 'sell_retail_monthly'
  | 'sell_retail_customer_monthly'
  | 'sell_wholesale_all_monthly'
  | 'sell_wholesale_monthly'
  | 'sell_all_monthly'

export type PdfMode = 'report' | 'receipts' | 'csv'

const portalAxios = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json' },
})

portalAxios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(PORTAL_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export function getPortalToken(): string | null {
  const token = sessionStorage.getItem(PORTAL_TOKEN_KEY)
  const expires = sessionStorage.getItem(PORTAL_TOKEN_EXPIRES_KEY)
  if (!token || !expires) return null
  if (Date.now() >= Number(expires)) {
    clearPortalSession()
    return null
  }
  return token
}

export function clearPortalSession() {
  sessionStorage.removeItem(PORTAL_TOKEN_KEY)
  sessionStorage.removeItem(PORTAL_TOKEN_EXPIRES_KEY)
}

function storePortalSession(accessToken: string, expiresIn: number) {
  sessionStorage.setItem(PORTAL_TOKEN_KEY, accessToken)
  sessionStorage.setItem(PORTAL_TOKEN_EXPIRES_KEY, String(Date.now() + expiresIn * 1000))
}

export async function verifyExportPortalPin(pin: string) {
  const res = await portalAxios.post<{ accessToken: string; expiresIn: number }>(
    '/export-portal/verify',
    { pin },
  )
  storePortalSession(res.data.accessToken, res.data.expiresIn)
  return res.data
}

export async function fetchExportPortalCustomers(
  filters: Pick<ExportPortalFilters, 'year' | 'month' | 'bill_type' | 'location'>,
) {
  const res = await portalAxios.get<{ data: ExportPortalCustomerOption[] }>(
    '/export-portal/customers',
    { params: filters },
  )
  return res.data
}

export async function fetchExportPortalBills(filters: ExportPortalFilters & { page?: number; pageSize?: number }) {
  const params = {
    ...filters,
    customer_id: filters.customer_id || undefined,
  }
  const res = await portalAxios.get<{
    data: ExportPortalBillSummary[]
    meta: { total: number; currentPage: number; totalPages: number }
  }>('/export-portal/bills', { params })
  return res.data
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function exportPortalZip(payload: ExportPortalFilters & {
  template: BillExportTemplate
  pdf_mode: PdfMode
}) {
  const body = {
    ...payload,
    customer_id: payload.customer_id || undefined,
  }
  const res = await portalAxios.post<Blob>('/export-portal/export', body, {
    responseType: 'blob',
    timeout: 300_000,
  })
  const blob = res.data
  if (blob instanceof Blob) {
    const filename = `bills-export-${payload.year}-${String(payload.month).padStart(2, '0')}-${payload.bill_type}-${payload.template}-${payload.pdf_mode}.zip`
    downloadBlob(filename, blob)
  }
}

/** Admin-only (uses staff JWT via DlInstance) */
export async function getExportPortalStatus() {
  const res = await DlInstance.get<{ configured: boolean }>('/export-portal/status')
  return res.data
}

export async function setExportPortalPin(pin: string) {
  const res = await DlInstance.patch<{ configured: true }>('/export-portal/pin', { pin })
  return res.data
}

export function defaultTemplateForBillType(billType: 'buy' | 'sell'): BillExportTemplate {
  return billType === 'buy' ? 'buy_monthly' : 'sell_all_monthly'
}

export function templatesForBillType(billType: 'buy' | 'sell'): BillExportTemplate[] {
  if (billType === 'buy') return ['buy_monthly']
  // Customer scope is handled by the customer filter — keep only layout/mode templates.
  return ['sell_all_monthly', 'sell_retail_monthly', 'sell_wholesale_all_monthly']
}
